import { supabase } from './supabase';
import { SupabaseClient } from '@supabase/supabase-js';
import { ServiceAddon } from './services';
import { createBookingScheduledMilestone } from './bookingMilestones';

// Assert that supabase is not null
const client = supabase as SupabaseClient;

let statusChangeCallback: ((booking: Booking) => void) | null = null;

export function subscribeToBookingStatusChanges(callback: (booking: Booking) => void) {
  console.log('Setting up booking status change subscription with callback:', !!callback);
  statusChangeCallback = callback;
  
  const channel = client
    .channel('booking-status-changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'bookings'
      },
      async (payload: any) => {
        console.log('Received database event:', {
          event: payload.eventType,
          old: payload.old,
          new: payload.new,
          table: payload.table,
          schema: payload.schema
        });

        // Only process UPDATE events
        if (payload.eventType !== 'UPDATE') {
          console.log('Ignoring non-UPDATE event:', payload.eventType);
          return;
        }

        // Check if status has changed
        if (payload.old?.status !== payload.new?.status) {
          console.log('Status change detected:', {
            oldStatus: payload.old?.status,
            newStatus: payload.new?.status,
            bookingId: payload.new?.id,
            timestamp: new Date().toISOString()
          });

          try {
            const booking = await getBooking(payload.new.id);
            console.log('Fetched booking details for automation:', {
              id: booking?.id,
              status: booking?.status,
              previousStatus: booking?.previous_status,
              hasCallback: !!statusChangeCallback,
              hasCustomer: !!booking?.customer,
              customerPhone: booking?.customer?.phone,
              timestamp: new Date().toISOString()
            });

            if (booking && statusChangeCallback) {
              console.log('Invoking automation callback');
              statusChangeCallback(booking);
            } else {
              console.log('Skipping automation callback:', {
                hasBooking: !!booking,
                hasCallback: !!statusChangeCallback
              });
            }
          } catch (error) {
            console.error('Error processing booking status change:', error);
          }
        } else {
          console.log('No status change detected:', {
            status: payload.new?.status,
            bookingId: payload.new?.id,
            timestamp: new Date().toISOString()
          });
        }
      }
    )
    .subscribe((status) => {
      console.log('Subscription status:', status);
    });

  return channel;
}

export interface CalendarBooking {
  id: string;
  title: string;
  booking_number: number;
  start: string;
  end: string;
  backgroundColor: string;
  borderColor: string;
  secondaryColor?: string | null;
  allDay?: boolean;
  display?: string;
  resourceId?: string;
  extendedProps: {
    customer: string;
    status: string;
    booking_number: number;
    subtotal: number;
    total_amount: number;
    discounts?: BookingDiscount[];
    payments?: Payment[];
    services?: BookingService[];
    resources?: Array<{
      id: string;
      first_name: string;
      last_name: string;
      avatar_url?: string;
    }>;
    is_buffer_block?: boolean;
    buffer_time?: number;
    booking_id?: string;
    resource_id?: string;
    mobile_booking?: boolean;
    customer_notes?: string;
    secondaryColor?: string;
    drop_off_time?: string | null;
    start_time?: string | null;
    booking_address?: string | null;
  };
}

// Helper to ensure valid date/time string
const formatDateTime = (date: string, time: string): string => {
  // Ensure date and time are strings
  const dateStr = String(date);
  const timeStr = String(time);
  // Add seconds if not present
  const timeWithSeconds = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
  // Return ISO string
  return `${dateStr}T${timeWithSeconds}`;
};

// Helper to get exclusive end date for all-day/multi-day events
const getExclusiveEnd = (start: string, end: string, allDay: boolean) => {
  if (!allDay) return end;
  // For all-day/multi-day, add one day to the end date
  const endDate = new Date(end);
  endDate.setDate(endDate.getDate() + 1);
  return endDate.toISOString().split('T')[0];
};

export async function getCalendarBookings(businessId: string, startDate: Date, endDate: Date, isLocationBooking: boolean, filterByResourceId?: string): Promise<CalendarBooking[]> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  console.log('📅 getCalendarBookings called with:', {
    businessId,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    isLocationBooking,
    filterByResourceId,
    isFiltering: !!filterByResourceId
  });

  // Format dates for the query
  const formattedStartDate = startDate.toISOString().split('T')[0];
  const formattedEndDate = endDate.toISOString().split('T')[0];

  console.log('Formatted dates for query:', {
    formattedStartDate,
    formattedEndDate
  });

  let bookings;
  let error;

  if (filterByResourceId) {
    console.log('📅 Filtering by resource ID:', filterByResourceId);
    // If filtering by resource, we need to first get the booking IDs that have this resource
    const { data: resourceBookings, error: resourceError } = await supabase
      .from('booking_resources')
      .select('booking_id')
      .eq('resource_id', filterByResourceId);

    console.log('📅 Resource bookings query result:', {
      resourceBookings,
      resourceError,
      bookingCount: resourceBookings?.length || 0
    });

    if (resourceError) {
      error = resourceError;
      bookings = null;
    } else {
      const bookingIds = resourceBookings?.map(rb => rb.booking_id) || [];
      console.log('📅 Found booking IDs for resource:', bookingIds);
      
      if (bookingIds.length === 0) {
        console.log('📅 No bookings found for this resource - showing empty calendar');
        // No bookings for this resource
        bookings = [];
        error = null;
      } else {
        // Get the full booking data for these booking IDs
        const { data: fullBookings, error: bookingsError } = await supabase
          .from('bookings')
          .select(`
            *,
            customer:customers(
              first_name,
              last_name,
              email,
              phone
            ),
            services:booking_services(
              *,
              service:services(*),
              vehicle:customer_vehicles(*)
            ),
            payments:payment_details(*),
            resources:booking_resources(
              resource:resources(
                id,
                first_name,
                last_name,
                avatar_url
              )
            )
          `)
          .eq('business_id', businessId)
          .eq('mobile_booking', !isLocationBooking)
          .or(`start_date.gte.${formattedStartDate},end_date.lte.${formattedEndDate}`)
          .in('id', bookingIds)
          .order('start_date', { ascending: true });

        bookings = fullBookings;
        error = bookingsError;
      }
    }
  } else {
    // Normal query without resource filtering
    const { data: allBookings, error: bookingsError } = await supabase
      .from('bookings')
      .select(`
        *,
        customer:customers(
          first_name,
          last_name,
          email,
          phone
        ),
        services:booking_services(
          *,
          service:services(*),
          vehicle:customer_vehicles(*)
        ),
        payments:payment_details(*),
        resources:booking_resources(
          resource:resources(
            id,
            first_name,
            last_name,
            avatar_url,
            compensation_type,
            commission_rate,
            commission_percentage
          )
        )
      `)
      .eq('business_id', businessId)
      .eq('mobile_booking', !isLocationBooking)
      .or(`start_date.gte.${formattedStartDate},end_date.lte.${formattedEndDate}`)
      .order('start_date', { ascending: true });

    bookings = allBookings;
    error = bookingsError;
  }

  if (error) {
    console.error('Error fetching calendar bookings:', error);
    throw error;
  }

  console.log('Raw bookings from database:', bookings);

  if (!bookings) return [];
  
  // Get color based on status
  const getStatusColor = (status: string, booking: any) => {
    // For cancelled bookings, always use pale red regardless of any other colors
    if (status === 'cancelled') {
      return '#fee2e2'; // Pale red (red-100)
    }
    
    // For scheduled bookings, use the booking's color if available
    if (status === 'scheduled' && booking.color) {
      return booking.color;
    }
    
    // For in_progress, always use yellow
    if (status === 'in_progress') {
      return '#eab308'; // Yellow
    }
    
    // Use default status colors for other statuses
    switch (status) {
      case 'pending':
        return '#a855f7'; // Purple
      case 'scheduled':
        return '#3b82f6'; // Blue (default)
      case 'completed':
        return '#22c55e'; // Green
      default:
        return '#3b82f6'; // Default blue
    }
  };

  // Process regular bookings
  const regularBookings = bookings.map(booking => {
    // Ensure valid start and end dates/times
    const start = formatDateTime(booking.start_date, booking.start_time);
    let end = booking.end_date && booking.end_time
      ? formatDateTime(booking.end_date, booking.end_time)
      : formatDateTime(booking.start_date, booking.start_time);
    // If multi-day (end date > start date), and times are 00:00, treat as all-day
    let allDay = false;
    if (booking.start_date !== booking.end_date) {
      const startTime = booking.start_time;
      const endTime = booking.end_time;
      if ((startTime === '00:00' || startTime === '00:00:00') && (endTime === '00:00' || endTime === '00:00:00')) {
        allDay = true;
        // For all-day/multi-day, set end to exclusive day after
        end = getExclusiveEnd(start, end, true);
      }
    }

    // Get the color based on status and service
    const color = getStatusColor(booking.status, booking);

    // Transform resources array to match the expected format
    const resources = booking.resources?.map((br: any) => ({
      id: br.resource.id,
      first_name: br.resource.first_name,
      last_name: br.resource.last_name,
      avatar_url: br.resource.avatar_url
    })) || [];

    // Get the primary resource ID (first one)
    const resourceId = resources.length > 0 ? resources[0].id : null;

    const processedBooking = {
      id: booking.id,
      title: booking.title || `Booking #${booking.booking_number}`,
      booking_number: booking.booking_number,
      start,
      end,
      backgroundColor: color,
      borderColor: color,
      secondaryColor: booking.secondary_color || null,
      allDay,
      display: 'block',
      resourceId,
      extendedProps: {
        customer: booking.customer 
          ? `${booking.customer.first_name} ${booking.customer.last_name}`
          : 'No Customer',
        status: booking.status,
        booking_number: booking.booking_number,
        subtotal: booking.subtotal || 0,
        total_amount: booking.total_amount || 0,
        payments: booking.payments || [],
        services: booking.services || [],
        resources: resources,
        is_buffer_block: false,
        mobile_booking: booking.mobile_booking,
        resource_id: resourceId,
        customer_notes: booking.customer_notes,
        secondaryColor: booking.secondary_color || null,
        drop_off_time: booking.drop_off_time || null,
        start_time: booking.start_time || null,
        booking_address: booking.booking_address || null
      }
    };

    console.log('Processed booking:', processedBooking);
    return processedBooking;
  });

  console.log('Final processed bookings:', regularBookings);
  return regularBookings;
}

export interface BookingService {
  id: string;
  booking_id: string;
  business_id: string;
  service_id: string;
  vehicle_id: string;
  name: string;
  description: string | null;
  price: number;
  tax_rate: number;
  tax_exempt: boolean;
  tax_amount: number;
  total_amount: number;
  duration_minutes: number;
  status: string;
  notes: string | null;
  start_date: string | null;
  end_date: string | null;
  vehicle_make: string | null;
  vehicle_model: string | null;
  vehicle_year: number | null;
  vehicle_color: string | null;
  discount_amount?: number;
  addons?: ServiceAddon[];
  created_at?: string;
  updated_at?: string;
}

interface BookingResource {
  id: string;
  booking_id: string;
  resource_id: string;
  service_id: string | null;
  role: string;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  updated_at: string;
  resource?: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    avatar_url?: string;
  };
}

interface BookingFlatRate {
  id: string;
  booking_id: string;
  item_id: string;
  resource_id: string;
  hours: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
  item_name?: string;
  item_description?: string;
  item_hours?: number;
  resource_first_name?: string;
  resource_last_name?: string;
  resource_email?: string;
}

export interface BookingMaterial {
  id: string;
  booking_id: string;
  material_id: string;
  material_name: string;
  material_description?: string;
  quantity: number;
  unit: string;
  cost: number;
  total_cost: number;
  created_at: string;
  updated_at: string;
}

export interface BookingDiscount {
  id: string;
  booking_id: string;
  title: string;
  type: string;
  amount: number;
  created_at: string;
  updated_at: string;
}

export interface Payment {
  id: string;
  booking_id: string;
  amount: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface Booking {
  id: string;
  title: string;
  status: string;
  subtotal: number;
  discount_amount?: number;
  end_date?: string;
  booking_number?: string;
  flat_rate_earnings?: number;
  estimated_flat_rate_earnings?: number;
  hourly_earnings?: number;
  estimated_hourly_earnings?: number;
  supplies_cost?: number;
  fixed_expenses?: number;
  services?: BookingService[];
  customer?: {
    id: string;
    created_at: string;
    first_name?: string;
    last_name?: string;
    email?: string | null;
    phone?: string;
    vehicles?: CustomerVehicle[];
  };
  resources?: BookingResource[];
  materials?: BookingMaterial[];
  discounts?: BookingDiscount[];
  payments?: Payment[];
  flat_rates?: BookingFlatRate[];
  tax_amount?: number;
  total_amount?: number;
  business_id: string;
  booking_address?: string;
  mobile_booking?: boolean;
  created_at: string;
  updated_at?: string;
  completed_at?: string;
  previous_status?: string;
  feedback_token?: string;
  
  // Additional fields for bookings
  color?: string;
  secondary_color?: string;
  drop_off_date?: string;
  drop_off_time?: string;
  start_date?: string;
  start_time?: string;
  end_time?: string;
  notes?: string;
  customer_notes?: string;
}

interface CustomerVehicle {
  id: string;
  customer_id: string;
  make: string;
  model: string;
  year: number;
  vin: string;
  color: string;
  license_plate: string;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

export async function getBookings(businessId: string, options?: { limit?: number; offset?: number }) {
  if (!businessId || typeof businessId !== 'string') {
    throw new Error('Valid business ID is required');
  }

  const { limit, offset = 0 } = options || {};

  console.log('Fetching bookings for business:', businessId, { limit, offset });
  
  let query = supabase
    .from('bookings')
    .select(`
      *,
      customer:customers(
        id,
        first_name,
        last_name,
        email,
        phone
      ),
      discounts:booking_discounts(*),
      services:booking_services(*),
      resources:booking_resources(
        *,
        resource:resources(
          id,
          first_name,
          last_name,
          email,
          avatar_url,
          compensation_type,
          commission_rate,
          commission_percentage
        )
      ),
      flat_rates:booking_flat_rates_with_details(*),
      payments:payment_details(*)
    `)
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  // Only apply range if limit is specified
  if (limit !== undefined) {
    query = query.range(offset, offset + limit - 1);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching bookings:', error);
    throw error;
  }

  return data as Booking[];
}

export async function getBookingsByCustomer(businessId: string, customerId: string, options?: { limit?: number; offset?: number }) {
  if (!businessId || typeof businessId !== 'string') {
    throw new Error('Valid business ID is required');
  }
  
  if (!customerId || typeof customerId !== 'string') {
    throw new Error('Valid customer ID is required');
  }

  const { limit = 50, offset = 0 } = options || {};

  console.log('Fetching bookings for customer:', customerId, { businessId, limit, offset });
  
  const { data, error } = await supabase
    .from('bookings')
    .select(`
      *,
      customer:customers(
        id,
        first_name,
        last_name,
        email,
        phone
      ),
      discounts:booking_discounts(*),
      services:booking_services(*),
      resources:booking_resources(
        *,
        resource:resources(
          id,
          first_name,
          last_name,
          email,
          avatar_url,
          compensation_type,
          commission_rate,
          commission_percentage
        )
      ),
      flat_rates:booking_flat_rates_with_details(*),
      payments:payment_details(*)
    `)
    .eq('business_id', businessId)
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    console.error('Error fetching bookings for customer:', error);
    throw error;
  }

  return data as Booking[];
}

// Get total count of bookings for pagination
export async function getBookingsCount(businessId: string): Promise<number> {
  if (!businessId || typeof businessId !== 'string') {
    throw new Error('Valid business ID is required');
  }

  const { count, error } = await supabase
    .from('bookings')
    .select('*', { count: 'exact', head: true })
    .eq('business_id', businessId);

  if (error) {
    console.error('Error counting bookings:', error);
    throw error;
  }

  return count || 0;
}

export async function getBooking(id: string) {
  if (!id || typeof id !== 'string') {
    throw new Error('Valid booking ID is required');
  }

  console.log('Fetching booking details:', { id });

  const { data, error } = await supabase
    .from('bookings')
    .select(`
      *,
      customer:customers(
        id,
        first_name, 
        last_name, 
        email, 
        phone,
        vehicles:customer_vehicles(*)
      ),
      discounts:booking_discounts(*),
      services:booking_services_with_details(*),
      materials:booking_materials_with_details(*),
      resources:booking_resources(
        *,
        resource:resources(
          id,
          first_name,
          last_name,
          email,
          phone,
          avatar_url,
          compensation_type,
          commission_rate,
          commission_percentage
        )
      ),
      flat_rates:booking_flat_rates_with_details(*),
      payments:payment_details(*)
    `)
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching booking:', {
      id,
      error: error.message,
      details: error.details,
      hint: error.hint
    });
    throw error;
  }

  if (!data) {
    console.error('Booking not found:', { id });
    throw new Error('Booking not found');
  }

  // Validate customer data
  if (!data.customer || !data.customer.id) {
    console.error('Invalid customer data:', {
      bookingId: id,
      customer: data.customer
    });
    throw new Error(`Invalid customer data for booking ${id}`);
  }

  const booking = data as Booking;
  
  console.log('Booking loaded:', {
    id: booking.id,
    customer: booking.customer ? `${booking.customer.first_name} ${booking.customer.last_name}` : 'No customer',
    customerId: booking.customer?.id,
    serviceCount: booking.services?.length || 0,
    resourceCount: booking.resources?.length || 0
  });

  return booking;
}

interface CreateBookingParams {
  business_id: string;
  customer_id: string;
  vehicles?: {
    vehicle_id: string;
    services: {
      service_id?: string;
      name: string;
      description?: string;
      price: number;
      tax_exempt?: boolean;
      tax_rate?: number;
      tax_amount?: number;
      discount_amount?: number;
      total_amount?: number;
      duration_minutes: number;
      start_date?: string;
      end_date?: string;
      notes?: string;
      resource_id?: string;
    }[];
    resource_id?: string;
  }[];
  resources?: {
    resource_id: string;
    service_id?: string;
    role?: string;
    start_date?: string;
    end_date?: string;
  }[];
  resource_id?: string;
  materials?: BookingMaterial[];
  flat_rates?: BookingFlatRate[];
  discounts?: BookingDiscount[];
  title?: string;
  status?: string;
  color?: string;
  secondary_color?: string;
  drop_off_date?: string;
  drop_off_time?: string;
  start_date?: string;
  start_time?: string;
  end_date?: string;
  end_time?: string;
  location?: string;
  notes?: string;
  booking_address?: string;
  customer_notes?: string;
  buffer_time?: number;
}

export async function createBooking(
  params: CreateBookingParams
) {
  // Create booking
  const { data: bookingData, error: bookingError } = await supabase
    .from('bookings')
    .insert([{
      business_id: params.business_id,
      customer_id: params.customer_id,
      title: params.title,
      status: params.status || 'scheduled',
      color: params.color,
      secondary_color: params.secondary_color,
      drop_off_date: params.drop_off_date,
      drop_off_time: params.drop_off_time,
      start_date: params.start_date,
      start_time: params.start_time,
      end_date: params.end_date,
      end_time: params.end_time,
      location: params.location,
      notes: params.notes,
      customer_notes: params.customer_notes,
      booking_address: params.booking_address
    }])
    .select()
    .single();

  if (bookingError) throw bookingError;

  // Create status history entry
  const { error: statusError } = await supabase
    .from('booking_status_history')
    .insert({
      booking_id: bookingData.id,
      status: params.status || 'scheduled',
      business_id: params.business_id
    });

  if (statusError) {
    console.error('Error creating status history:', statusError);
    // Don't throw here as the booking was created successfully
  }

  // Create booking resources if provided
  if (params.resources?.length) {
    const { error: resourcesError } = await supabase
      .from('booking_resources')
      .insert(
        params.resources.map(resource => ({
          booking_id: bookingData.id,
          resource_id: resource.resource_id,
          role: resource.role || 'technician',
          start_date: `${params.start_date}T${params.start_time}`,
          end_date: `${params.end_date}T${params.end_time}`
        }))
      );

    if (resourcesError) throw resourcesError;
  }

  // Create booking services for each vehicle if provided
  if (params.vehicles) {
    for (const vehicle of params.vehicles) {
      // Create services for this vehicle
      const { error: servicesError } = await supabase
        .from('booking_services')
        .insert(
          vehicle.services.map(service => {
            const taxRate = service.tax_rate || 5;
            const taxAmount = service.tax_exempt ? 0 : Math.round((service.price * taxRate / 100) * 100) / 100;
            const discountAmount = service.discount_amount || 0;
            const taxAmountFinal = service.tax_exempt ? 0 : taxAmount;
            const totalAmount = Math.round((service.price - discountAmount + taxAmountFinal) * 100) / 100;

            return {
              business_id: params.business_id,
              booking_id: bookingData.id,
              service_id: service.service_id,
              vehicle_id: vehicle.vehicle_id,
              name: service.name,
              description: service.description,
              price: service.price,
              tax_exempt: service.tax_exempt || false,
              tax_rate: taxRate,
              tax_amount: taxAmountFinal,
              discount_amount: discountAmount,
              total_amount: totalAmount,
              duration_minutes: service.duration_minutes,
              start_date: `${params.start_date} ${params.start_time}`,
              end_date: `${params.end_date} ${params.end_time}`,
              status: 'pending',
              notes: service.description
            };
          })
        );

      if (servicesError) throw servicesError;
    }
  }

  // Create booking resources for services
  if (params.vehicles) {
    for (const vehicle of params.vehicles) {
      for (const service of vehicle.services) {
        if (service.resource_id) {
          const { error: resourceError } = await supabase
            .from('booking_resources')
            .insert({
              booking_id: bookingData.id,
              resource_id: service.resource_id,
              service_id: service.service_id,
              role: 'technician',
              start_date: `${params.start_date} ${params.start_time}`,
              end_date: `${params.end_date} ${params.end_time}`
            });

          if (resourceError) throw resourceError;
        }
      }
    }
  }

  // Create overall booking resource if specified
  if (params.resource_id) {
    const { error: resourcesError } = await supabase
      .from('booking_resources')
      .insert({
        booking_id: bookingData.id,
        resource_id: params.resource_id,
        role: 'lead',
        start_date: `${params.start_date} ${params.start_time}`,
        end_date: `${params.end_date} ${params.end_time}`
      });

    if (resourcesError) throw resourcesError;
  }

  // Copy materials if provided
  if (params.materials?.length) {
    const { error: materialsError } = await supabase
      .from('booking_materials')
      .insert(
        params.materials.map(material => ({
          booking_id: bookingData.id,
          material_id: material.material_id,
          material_name: material.material_name,
          material_description: material.material_description,
          quantity: material.quantity,
          unit: material.unit,
          cost: material.cost,
          total_cost: material.total_cost
        }))
      );

    if (materialsError) throw materialsError;
  }

  // Copy flat rates if provided
  if (params.flat_rates?.length) {
    const { error: flatRatesError } = await supabase
      .from('booking_flat_rates')
      .insert(
        params.flat_rates.map(rate => ({
          booking_id: bookingData.id,
          item_id: rate.item_id,
          resource_id: rate.resource_id,
          hours: rate.hours,
          notes: rate.notes
        }))
      );

    if (flatRatesError) throw flatRatesError;
  }

  // Copy discounts if provided
  if (params.discounts?.length) {
    const { error: discountsError } = await supabase
      .from('booking_discounts')
      .insert(
        params.discounts.map(discount => ({
          booking_id: bookingData.id,
          title: discount.title,
          type: discount.type,
          amount: discount.amount
        }))
      );

    if (discountsError) throw discountsError;
  }

  // Create booking scheduled milestone for tracking
  try {
    // If the booking has a scheduled date/time, create a scheduled milestone
    if (params.start_date && params.start_time && params.status === 'scheduled') {
      await createBookingScheduledMilestone(
        params.business_id,
        params.customer_id,
        bookingData.id,
        params.start_date,
        params.start_time
      );
    }
  } catch (milestoneError) {
    console.error('Error creating booking milestone:', milestoneError);
    // Don't throw here as the booking was created successfully
  }

  return getBooking(bookingData.id);
}

export async function updateBooking(
  id: string,
  updates: Partial<Booking> & {
    services?: {
      id?: string;
      service_id: string;
      vehicle_id: string;
      name: string;
      description?: string;
      price: number;
      tax_exempt?: boolean;
      tax_rate?: number;
      tax_amount?: number;
      discount_amount?: number;
      total_amount?: number;
      duration_minutes: number;
      start_date?: string;
      end_date?: string;
      notes?: string;
    }[];
    resources?: {
      id?: string;
      resource_id: string;
      service_id?: string;
      role?: string;
      start_date?: string;
      end_date?: string;
    }[];
    flat_rates?: {
      id?: string;
      item_id: string;
      resource_id: string;
      hours: number;
      notes?: string;
    }[];
  }
) {
  const { services, resources, flat_rates, ...bookingUpdates } = updates;

  console.log('Updating booking:', {
    id,
    updates: {
      bookingUpdates,
      services: services?.length,
      resources: resources?.length,
      flat_rates: flat_rates?.length
    }
  });

  // First check if this is a mobile booking
  const { data: bookingData } = await supabase
    .from('bookings')
    .select('mobile_booking')
    .eq('id', id)
    .single();

  const isMobileBooking = bookingData?.mobile_booking;

  // Update booking
  const { error: bookingError } = await supabase
    .from('bookings')
    .update(bookingUpdates)
    .eq('id', id);

  if (bookingError) throw bookingError;

  // If this is a mobile booking and we're updating dates/times, also update mobile_bookings table
  if (isMobileBooking && (bookingUpdates.start_date || bookingUpdates.start_time || bookingUpdates.end_date || bookingUpdates.end_time)) {
    const { error: mobileBookingError } = await supabase
      .from('mobile_bookings')
      .update({
        start_date: bookingUpdates.start_date,
        start_time: bookingUpdates.start_time,
        end_date: bookingUpdates.end_date,
        end_time: bookingUpdates.end_time
      })
      .eq('booking_id', id);

    if (mobileBookingError) throw mobileBookingError;
  }

  // Update services if provided
  if (services) {
    console.log('Updating services:', services.length);

    // Delete existing services
    const { error: deleteServicesError } = await supabase
      .from('booking_services')
      .delete()
      .eq('booking_id', id);

    if (deleteServicesError) throw deleteServicesError;

    // Insert new services
    if (services.length > 0) {
      const { error: servicesError } = await supabase
        .from('booking_services')
        .insert(services.map(service => {
          const taxAmount = service.tax_exempt ? 0 : Math.round((service.price * (service.tax_rate || 5) / 100) * 100) / 100;
          const discountAmount = service.discount_amount || 0;
          const taxAmountFinal = service.tax_exempt ? 0 : taxAmount;
          const totalAmount = Math.round((service.price - discountAmount + taxAmountFinal) * 100) / 100;

          return {
            booking_id: id,
            ...service,
            tax_amount: taxAmountFinal,
            total_amount: totalAmount
          };
        }));

      if (servicesError) {
        console.error('Error inserting services:', servicesError);
        throw servicesError;
      }
    }
  }

  // Update resources if provided
  if (resources) {
    console.log('Updating resources:', resources.length);

    // Delete existing resources
    const { error: deleteResourcesError } = await supabase
      .from('booking_resources')
      .delete()
      .eq('booking_id', id);

    if (deleteResourcesError) throw deleteResourcesError;

    // Insert new resources
    if (resources.length > 0) {
      const { error: resourcesError } = await supabase
        .from('booking_resources')
        .insert(
          resources.map(resource => ({
            booking_id: id,
            ...resource
          }))
        );

      if (resourcesError) throw resourcesError;
    }
  }

  // Update flat rates if provided
  if (flat_rates) {
    console.log('Updating flat rates:', {
      newFlatRatesCount: flat_rates.length
    });

    try {
      // First delete all existing flat rates
      const { error: deleteError } = await supabase
        .from('booking_flat_rates')
        .delete()
        .eq('booking_id', id);

      if (deleteError) {
        console.error('Error deleting flat rates:', deleteError);
        throw deleteError;
      }

      // Then insert the new flat rates
      const { data: insertedRates, error: insertError } = await supabase
        .from('booking_flat_rates')
        .insert(
          flat_rates.map(flatRate => ({
            booking_id: id,
            item_id: flatRate.item_id,
            resource_id: flatRate.resource_id,
            hours: flatRate.hours,
            notes: flatRate.notes
          }))
        )
        .select();

      if (insertError) {
        console.error('Error inserting flat rates:', insertError);
        throw insertError;
      }

      console.log('Flat rates updated successfully:', {
        insertedCount: insertedRates?.length || 0
      });
    } catch (err) {
      console.error('Error updating flat rates:', err);
      throw err;
    }
  }

  console.log('Booking update completed, fetching updated booking');
  return getBooking(id);
}

export async function deleteBooking(id: string) {
  try {
    console.log('Starting deletion of booking:', id);

    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }

    const client = supabase;
    
    // Delete mobile booking record first
    const { error: mobileBookingError } = await client
      .from('mobile_bookings')
      .delete()
      .eq('booking_id', id);

    if (mobileBookingError) {
      console.error('Error deleting mobile booking:', mobileBookingError);
      throw mobileBookingError;
    }

    // Delete related records
    const { error: servicesError } = await client
      .from('booking_services')
      .delete()
      .eq('booking_id', id);

    if (servicesError) {
      console.error('Error deleting booking services:', servicesError);
      throw servicesError;
    }

    const { error: materialsError } = await client
      .from('booking_materials')
      .delete()
      .eq('booking_id', id);

    if (materialsError) {
      console.error('Error deleting booking materials:', materialsError);
      throw materialsError;
    }

    const { error: resourcesError } = await client
      .from('booking_resources')
      .delete()
      .eq('booking_id', id);

    if (resourcesError) {
      console.error('Error deleting booking resources:', resourcesError);
      throw resourcesError;
    }

    const { error: flatRatesError } = await client
      .from('booking_flat_rates')
      .delete()
      .eq('booking_id', id);

    if (flatRatesError) {
      console.error('Error deleting booking flat rates:', flatRatesError);
      throw flatRatesError;
    }

    const { error: discountsError } = await client
      .from('booking_discounts')
      .delete()
      .eq('booking_id', id);

    if (discountsError) {
      console.error('Error deleting booking discounts:', discountsError);
      throw discountsError;
    }

    const { error: paymentsError } = await client
      .from('payments')
      .delete()
      .eq('booking_id', id);

    if (paymentsError) {
      console.error('Error deleting booking payments:', paymentsError);
      throw paymentsError;
    }

    // Finally delete the booking
    const { error: bookingError } = await client
      .from('bookings')
      .delete()
      .eq('id', id);

    if (bookingError) {
      console.error('Error deleting booking:', bookingError);
      throw bookingError;
    }

    console.log('Successfully deleted booking:', id);
  } catch (err) {
    console.error('Error in deleteBooking:', err);
    throw err;
  }
}

async function progressBookingStatus(id: string) {
  // Get current booking status
  const { data: booking, error: fetchError } = await supabase
    .from('bookings')
    .select('status')
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;

  // Determine next status
  const nextStatus = (() => {
    switch (booking.status) {
      case 'pending':
        return 'scheduled';
      case 'scheduled':
        return 'in_progress';
      case 'in_progress':
        return 'completed';
      default:
        return booking.status;
    }
  })();

  // Update booking status
  const { error: updateError } = await supabase
    .from('bookings')
    .update({ status: nextStatus })
    .eq('id', id);

  if (updateError) throw updateError;

  return nextStatus;
}

async function getBookingStats(businessId: string) {
  const { data: bookings, error } = await supabase
    .from('bookings')
    .select(`
      *,
      services:booking_services(total_amount)
    `)
    .eq('business_id', businessId);

  if (error) throw error;

  const stats = {
    totalBookings: bookings.length,
    completedBookings: bookings.filter(b => b.status === 'completed').length,
    totalRevenue: bookings.reduce((sum, booking) => 
      sum + booking.services.reduce((serviceSum, service) => serviceSum + service.total_amount, 0)
    , 0),
    averageBookingValue: bookings.length > 0 
      ? bookings.reduce((sum, booking) => 
          sum + booking.services.reduce((serviceSum, service) => serviceSum + service.total_amount, 0)
        , 0) / bookings.length 
      : 0
  };

  return stats;
}

export async function createMobileBooking(params: CreateBookingParams) {
  if (!supabase) throw new Error('Supabase client is not initialized');

  try {
    console.log('Starting mobile booking creation with params:', {
      business_id: params.business_id,
      buffer_time: params.buffer_time,
      start_date: params.start_date,
      start_time: params.start_time,
      end_date: params.end_date,
      end_time: params.end_time
    });

    // First create the booking record
    const { data: bookingData, error: bookingError } = await supabase
      .from('bookings')
      .insert([{
        business_id: params.business_id,
        customer_id: params.customer_id,
        location: null,
        notes: params.notes || null,
        customer_notes: params.customer_notes || null,
        subtotal: 0, // Will be updated after services are added
        discount_amount: 0,
        tax_amount: 0,
        total_amount: 0,
        drop_off_date: null,
        drop_off_time: null,
        start_date: params.start_date,
        start_time: params.start_time,
        end_date: params.end_date,
        end_time: params.end_time,
        title: params.title,
        status: 'scheduled',
        source: 'in_person',
        source_data: null,
        source_identifier: null,
        material_costs: 0,
        labour_costs: 0,
        completed_at: null,
        color: params.color,
        secondary_color: params.secondary_color,
        mobile_booking: true,
        booking_address: params.booking_address
      }])
      .select()
      .single();

    if (bookingError) throw bookingError;
    if (!bookingData) throw new Error('No booking data returned');

    console.log('Main booking record created with ID:', bookingData.id);
    
    // Create the mobile_booking record for the main service
    const { error: mobileBookingError } = await supabase
      .from('mobile_bookings')
      .insert([{
        business_id: params.business_id,
        resource_id: params.resource_id,
        booking_id: bookingData.id,
        start_date: params.start_date,
        start_time: params.start_time,
        end_date: params.end_date,
        end_time: params.end_time,
        buffer_time: 0, // Main booking has 0 buffer time
        is_buffer_block: false // This is the main booking, not a buffer block
      }]);

    if (mobileBookingError) {
      console.error('Error creating mobile booking record:', mobileBookingError);
      throw mobileBookingError;
    }
    
    console.log('Main mobile_booking record created successfully');

    // Now explicitly check for buffer time and create a buffer block if needed
    const bufferTime = params.buffer_time !== undefined ? params.buffer_time : 0;
    console.log(`Buffer time for booking: ${bufferTime} minutes`);
    
    // If buffer time is set, create a separate buffer block
    if (bufferTime > 0) {
      console.log(`Creating buffer block with buffer_time: ${bufferTime} minutes`);
      
      if (!params.end_time) {
        console.error('Cannot create buffer block: end_time is missing');
      } else {
        try {
          // Calculate the buffer block start and end times (starts right after the booking ends)
          // Parse the end time
          const [endHours, endMinutes] = params.end_time.split(':').map(Number);
          
          // Create a date object with the end time
          const bufferStartDate = new Date();
          bufferStartDate.setHours(endHours, endMinutes, 0, 0);
          
          // Create a date object for the end of the buffer
          const bufferEndDate = new Date(bufferStartDate);
          bufferEndDate.setMinutes(bufferEndDate.getMinutes() + bufferTime);
          
          // Format the times back to strings
          const bufferStartTime = params.end_time; // Use the exact end time of the booking as the start of the buffer
          const bufferEndTime = `${String(bufferEndDate.getHours()).padStart(2, '0')}:${String(bufferEndDate.getMinutes()).padStart(2, '0')}`;
          
          console.log(`Buffer block time range: ${bufferStartTime} - ${bufferEndTime}`);
          
          // Create the buffer block data to be inserted
          const bufferBlockData = {
            business_id: params.business_id,
            resource_id: params.resource_id,
            booking_id: bookingData.id, // Use the same booking_id for relationship
            start_date: params.end_date, // Use booking end date as buffer start date
            start_time: bufferStartTime,  // Booking end time becomes buffer start time
            end_date: params.end_date,    // Assuming buffer doesn't cross midnight
            end_time: bufferEndTime,      // Calculated end time after buffer
            buffer_time: bufferTime, // Store actual buffer time
            is_buffer_block: true // Flag this as a buffer block for UI distinction
          };
          
          console.log('Creating buffer block with data:', JSON.stringify(bufferBlockData, null, 2));
          
          // Create a separate buffer block entry in mobile_bookings
          const { data: bufferBlockResult, error: bufferBlockError } = await supabase
            .from('mobile_bookings')
            .insert([bufferBlockData]);
            
          if (bufferBlockError) {
            console.error('Error creating buffer block:', bufferBlockError);
            // Don't throw, just log - the main booking was still created
          } else {
            console.log('Buffer block created successfully!');
            
            // Double check that the buffer block was created
            try {
              const { data: checkData, error: checkError } = await supabase
                .from('mobile_bookings')
                .select('*')
                .eq('booking_id', bookingData.id)
                .eq('is_buffer_block', true);
              
              if (checkError) {
                console.error('Error checking buffer block creation:', checkError);
              } else {
                console.log(`Found ${checkData?.length || 0} buffer blocks in database for booking ID ${bookingData.id}:`);
                if (checkData && checkData.length > 0) {
                  console.log('Buffer block details:', JSON.stringify(checkData, null, 2));
                } else {
                  console.error('NO BUFFER BLOCKS FOUND even though insert claimed to succeed!');
                  
                  // Try creating the buffer block again with all fields explicitly defined
                  console.log('Retrying buffer block creation with all fields defined...');
                  const retryData = await supabase
                    .from('mobile_bookings')
                    .insert([{
                      business_id: params.business_id,
                      resource_id: params.resource_id,
                      booking_id: bookingData.id,
                      start_date: params.end_date,
                      start_time: bufferStartTime,
                      end_date: params.end_date,
                      end_time: bufferEndTime,
                      buffer_time: bufferTime,
                      is_buffer_block: true,
                      created_at: new Date().toISOString()
                    }]);
                  
                  console.log('Retry result:', retryData);
                }
              }
            } catch (verifyError) {
              console.error('Error verifying buffer block creation:', verifyError);
            }
          }
        } catch (bufferError) {
          console.error('Error in buffer block creation:', bufferError);
          // Don't throw, just log the error
        }
      }
    } else {
      console.log('No buffer block created - buffer_time is', bufferTime);
    }

    // Create the booking_resources record
    const { error: bookingResourceError } = await supabase
      .from('booking_resources')
      .insert([{
        booking_id: bookingData.id,
        resource_id: params.resource_id,
        start_date: `${params.start_date} ${params.start_time}`,
        end_date: `${params.end_date} ${params.end_time}`
      }]);

    if (bookingResourceError) throw bookingResourceError;

    // Create booking services for each vehicle if provided
    if (params.vehicles) {
      let subtotal = 0;
      let taxAmount = 0;

      for (const vehicle of params.vehicles) {
        // Create services for this vehicle
        const { error: servicesError } = await supabase
          .from('booking_services')
          .insert(
            vehicle.services.map(service => {
              const taxRate = service.tax_rate || 5;
              const taxAmountService = service.tax_exempt ? 0 : Math.round((service.price * taxRate / 100) * 100) / 100;
              const discountAmount = service.discount_amount || 0;
              const taxAmountFinal = service.tax_exempt ? 0 : taxAmountService;
              const totalAmount = Math.round((service.price - discountAmount + taxAmountFinal) * 100) / 100;

              subtotal += service.price;
              taxAmount += taxAmountFinal;

              return {
                business_id: params.business_id,
                booking_id: bookingData.id,
                service_id: service.service_id,
                vehicle_id: vehicle.vehicle_id,
                name: service.name,
                description: service.description,
                price: service.price,
                tax_exempt: service.tax_exempt || false,
                tax_rate: taxRate,
                tax_amount: taxAmountFinal,
                discount_amount: discountAmount,
                total_amount: totalAmount,
                duration_minutes: service.duration_minutes,
                start_date: `${params.start_date} ${params.start_time}`,
                end_date: `${params.end_date} ${params.end_time}`,
                status: 'pending',
                notes: service.notes
              };
            })
          );

        if (servicesError) throw servicesError;
      }

      // Update the booking with calculated totals
      const totalAmount = subtotal + taxAmount;
      const { error: updateError } = await supabase
        .from('bookings')
        .update({
          subtotal,
          tax_amount: taxAmount,
          total_amount: totalAmount
        })
        .eq('id', bookingData.id);

      if (updateError) throw updateError;
    }

    console.log('Mobile booking creation completed successfully');
    
    // Create booking scheduled milestone for tracking
    try {
      // Create scheduled milestone for mobile bookings (they are always scheduled)
      if (params.start_date && params.start_time) {
        await createBookingScheduledMilestone(
          params.business_id,
          params.customer_id,
          bookingData.id,
          params.start_date,
          params.start_time
        );
      }
    } catch (milestoneError) {
      console.error('Error creating booking milestone for mobile booking:', milestoneError);
      // Don't throw here as the booking was created successfully
    }
    
    return getBooking(bookingData.id);
  } catch (error) {
    console.error('Error creating mobile booking:', error);
    throw error;
  }
}

export async function getBasicBookingBlocks(businessId: string, startDate: Date, endDate: Date, isLocationBooking: boolean, filterByResourceId?: string): Promise<CalendarBooking[]> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  // Format dates for the query
  const formattedStartDate = startDate.toISOString().split('T')[0];
  const formattedEndDate = endDate.toISOString().split('T')[0];

  // Fetch minimal fields needed for displaying blocks including customer name for better UX
  const { data: bookings, error } = await supabase
    .from('bookings')
    .select(`
      id, 
      booking_number, 
      title,
      start_date, 
      start_time, 
      end_date, 
      end_time, 
      status, 
      color, 
      secondary_color, 
      drop_off_time,
      customer:customers(first_name, last_name)
    `)
    .eq('business_id', businessId)
    .eq('mobile_booking', !isLocationBooking)
    .or(`start_date.gte.${formattedStartDate},end_date.lte.${formattedEndDate}`)
    .order('start_date', { ascending: true });

  if (error) {
    console.error('Error fetching basic booking blocks:', error);
    throw error;
  }

  if (!bookings) return [];
  
  // Simplified color mapping (same as full function)
  const getStatusColor = (status: string, booking: any) => {
    // For cancelled bookings, always use pale red regardless of any other colors
    if (status === 'cancelled') {
      return '#fee2e2'; // Pale red (red-100)
    }
    
    // For scheduled bookings, use the booking's color if available
    if (status === 'scheduled' && booking.color) {
      return booking.color;
    }
    
    // For in_progress, always use yellow
    if (status === 'in_progress') {
      return '#eab308'; // Yellow
    }
    
    // Use default status colors for other statuses
    switch (status) {
      case 'pending':
        return '#a855f7'; // Purple
      case 'scheduled':
        return '#3b82f6'; // Blue (default)
      case 'completed':
        return '#22c55e'; // Green
      default:
        return '#3b82f6'; // Default blue
    }
  };

  // Process bookings into basic blocks with immediate visual completion
  return bookings.map(booking => {
    const start = `${booking.start_date}T${booking.start_time}`;
    let end = booking.end_date && booking.end_time 
      ? `${booking.end_date}T${booking.end_time}`
      : start;
    let allDay = false;
    if (booking.start_date !== booking.end_date) {
      const startTime = booking.start_time;
      const endTime = booking.end_time;
      if ((startTime === '00:00' || startTime === '00:00:00') && (endTime === '00:00' || endTime === '00:00:00')) {
        allDay = true;
        end = getExclusiveEnd(start, end, true);
      }
    }
    
    const color = getStatusColor(booking.status, booking);
    
    // Create better display title and customer name
    const customerName = booking.customer 
      ? `${booking.customer.first_name} ${booking.customer.last_name}`
      : 'No Customer';
    
    const displayTitle = booking.title || `Booking #${booking.booking_number}`;
    
    return {
      id: booking.id,
      title: displayTitle,
      booking_number: booking.booking_number,
      start,
      end,
      backgroundColor: color,
      borderColor: color,
      secondaryColor: booking.secondary_color || null,
      allDay,
      display: 'block',
      extendedProps: {
        customer: customerName,
        status: booking.status,
        booking_number: booking.booking_number,
        // Remove isLoading flag so blocks appear in final state immediately
        drop_off_time: booking.drop_off_time || null,
        start_time: booking.start_time || null,
        secondary_color: booking.secondary_color || null,
        // Add minimal required fields for compatibility
        subtotal: 0,
        total_amount: 0,
        payments: [],
        services: [],
        resources: [],
        is_buffer_block: false,
        mobile_booking: !isLocationBooking,
        customer_notes: ''
      }
    };
  });
}